<script setup>
/**
 * GoalList
 * Unified component for displaying goals (current or past)
 * @prop {String} mode - 'current' or 'past'
 */
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import GoalCard from '@/ui/components/goals/GoalCard.vue'
import GoalLoadingOverlay from '@/ui/components/goals/GoalLoadingOverlay.vue'
import { useGoals } from '@/core/composables/useGoals'
import { useToast } from '@/core/composables/useToast'
import { useAuthStore } from '@/app/store/auth'
import { useConfirmDialog } from '@/core/composables/useConfirmDialog'

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: (value) => ['current', 'past'].includes(value),
  },
})

const emit = defineEmits(['edit-goal'])

const { success, error: toastError, warning } = useToast()
const { confirm } = useConfirmDialog()
const authStore = useAuthStore()
const {
  currentGoals,
  pastGoals,
  currentGoalsReady,
  pastGoalsReady,
  subscribeToCurrentGoals,
  subscribeToPastGoals,
  unsubscribeAll,
  updateGoalResults,
  deleteGoal,
} = useGoals()

// State
const loadingGoalId = ref(null)
const { isAdmin } = storeToRefs(authStore)

// Computed properties based on mode
const goals = computed(() => {
  return props.mode === 'current' ? currentGoals.value : pastGoals.value
})

const isReady = computed(() => {
  return props.mode === 'current' ? currentGoalsReady.value : pastGoalsReady.value
})

const headerText = computed(() => {
  if (props.mode === 'current') {
    return `יעדים פעילים נכון לתאריך: ${todayFormatted.value}`
  }
  return 'יעדים שהסתיימו'
})

const emptyStateIcon = computed(() => {
  if (props.mode === 'current') {
    return `
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M12 11v6M9 14h6" />
    `
  }
  return `
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  `
})

const emptyStateTitle = computed(() => {
  return props.mode === 'current' ? 'אין יעדים לתצוגה' : 'אין יעדים שהסתיימו'
})

const emptyStateMessage = computed(() => {
  return props.mode === 'current'
    ? 'צור יעד חדש כדי להתחיל'
    : 'יעדים שתאריך הסיום שלהם עבר יופיעו כאן'
})

// Initialize
onMounted(() => {
  if (props.mode === 'current') {
    subscribeToCurrentGoals()
  } else {
    subscribeToPastGoals()
  }
})

onUnmounted(() => {
  unsubscribeAll()
})

// Get today's date formatted
const todayFormatted = computed(() => {
  return new Date().toLocaleDateString('he')
})

// Handle loading data for a goal
async function handleLoadData(goal) {
  loadingGoalId.value = goal.uid

  try {
    const result = await updateGoalResults(goal)

    if (result.errorCount > 0) {
      warning(`עודכן עם שגיאות: ${result.successCount} הצליחו, ${result.errorCount} נכשלו`)
    } else {
      success('הנתונים עודכנו בהצלחה')
    }
  } catch (err) {
    toastError('שגיאה בטעינת נתונים')
    console.error(err)
  } finally {
    loadingGoalId.value = null
  }
}

// Handle edit goal
function handleEditGoal(goal) {
  emit('edit-goal', goal)
}

// Handle delete goal
async function handleDeleteGoal(goal) {
  const ok = await confirm({
    title: 'מחיקת יעד',
    message: 'האם אתה בטוח שברצונך למחוק את היעד?',
    confirmText: 'מחק',
    variant: 'danger',
  })
  if (!ok) return

  try {
    await deleteGoal(goal.uid)
    success('היעד נמחק בהצלחה')
  } catch (err) {
    toastError('שגיאה במחיקת היעד')
    console.error(err)
  }
}

// Handle agent click
function handleAgentClick({ agent, goal }) {
  // TODO: Open agent details modal/drawer
}
</script>

<template>
  <div class="goal-list" :class="`goal-list--${mode}`">
    <!-- Header Note -->
    <div class="header-note" :class="`header-note--${mode}`">
      <p>{{ headerText }}</p>
    </div>

    <!-- Loading Overlay -->
    <GoalLoadingOverlay :visible="!!loadingGoalId" />

    <!-- Loading State -->
    <div v-if="!isReady" class="goals-loading">
      <div class="goals-loading-spinner"></div>
      <p>טוען יעדים...</p>
    </div>

    <!-- No Goals Message -->
    <div v-else-if="goals.length === 0" class="no-goals">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        v-html="emptyStateIcon"
      ></svg>
      <h2>{{ emptyStateTitle }}</h2>
      <p>{{ emptyStateMessage }}</p>
    </div>

    <!-- Goals Grid -->
    <div v-else class="goals-grid">
      <GoalCard
        v-for="goal in goals"
        :key="goal.uid"
        :goal="goal"
        :is-admin="isAdmin"
        :loading="loadingGoalId === goal.uid"
        @load-data="handleLoadData"
        @edit="handleEditGoal"
        @delete="handleDeleteGoal"
        @agent-click="handleAgentClick"
      />
    </div>
  </div>
</template>

<style scoped>
.goal-list {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.header-note {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  position: sticky;
  top: 0;
  z-index: 5;
}

/* Current mode styling */
.header-note--current {
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%);
  border-radius: var(--border-radius);
}

.header-note--current p {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin: 0;
}

/* Past mode styling */
.header-note--past {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%);
  border: 1px solid var(--color-warning);
  border-radius: var(--border-radius);
}

.header-note--past p {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-warning);
  margin: 0;
}

.goals-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  color: var(--text-muted);
  padding: var(--spacing-xl);
}

.goals-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.goals-loading p {
  font-size: var(--font-size-sm);
  margin: 0;
}

.no-goals {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  color: var(--text-muted);
  padding: var(--spacing-xl);
}

.no-goals svg {
  opacity: 0.5;
}

.no-goals h2 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.no-goals p {
  font-size: var(--font-size-sm);
  margin: 0;
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
  align-items: start;
  gap: var(--spacing-lg);
  padding-bottom: var(--spacing-md);
}

@media (max-width: 1024px) {
  .goals-grid {
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: var(--spacing-md);
  }
}

@media (max-width: 768px) {
  .goals-grid {
    grid-template-columns: 1fr;
  }
}
</style>
